// @givmo/app-admin — the "Cover processing fees" row on the portal's company charge dialogs.
//
// The company deposit's row (CAM-BE11, D-CAM-37), lifted out so the Give and approve dialogs render
// exactly the same control (CAM-BE13, D-CAM-40): the design-system checkbox, a medium title and one
// quiet line under it, whose words come from `cover-fees-copy.ts` (one place for every row).
//
// A locked row dims WHOLE — box, title and line — so it never reads as live, except a line that says
// WHY it is locked (the wallet rail's "No processing fees…"), which stays legible. The design-system
// Checkbox dims its own box on `data-disabled`, so the row dims only the words beside it (dimming the
// label as well would dim the box twice). The wrapping <label> is the checkbox's accessible name, so
// no `aria-label` (it would stack).

import { Checkbox, cn, testIdProp } from '@givmo/design-system';

export interface CoverFeesRowProps {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  /** Locked: no quote to state, nothing to cover, the company covers every charge, or a charge is in doubt. */
  disabled: boolean;
  title: string;
  /** The line under the title, or `null` to state no figure. */
  line: string | null;
  /** The line says why the row is locked, so it stays legible while the box and title dim. */
  lineIsReason?: boolean;
  testId: string;
}

export function CoverFeesRow({
  checked,
  onCheckedChange,
  disabled,
  title,
  line,
  lineIsReason = false,
  testId,
}: CoverFeesRowProps) {
  return (
    <label
      className={cn('flex items-start gap-10', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
      data-disabled={disabled ? 'true' : undefined}
      {...testIdProp(testId)}
    >
      <Checkbox
        appearance="saas"
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="mt-2"
        testId={`${testId}-checkbox`}
      />
      <span className="flex min-w-0 flex-col gap-2">
        <span
          className={cn(
            'font-sans text-s14 font-w500 tracking-givmo text-text-strong',
            disabled && 'opacity-50',
          )}
          {...testIdProp(`${testId}-title`)}
        >
          {title}
        </span>
        {line !== null && (
          <span
            className={cn(
              'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary',
              disabled && !lineIsReason && 'opacity-50',
            )}
            {...testIdProp(`${testId}-line`)}
          >
            {line}
          </span>
        )}
      </span>
    </label>
  );
}
